/**
 * Browser error reporting.
 *
 * The Laravel SDK only sees server-side exceptions, so anything that breaks in
 * the browser (a failed render, a dead script, a rejected fetch) never reached
 * Sentry. This closes that gap without paying for it on first paint.
 *
 * The SDK is ~25KB gzipped against a bundle that is already carrying Alpine,
 * GSAP and Lenis, so loading it up front would be a real cost on a storefront.
 * Instead a tiny listener is armed immediately to buffer whatever breaks early,
 * the SDK is fetched once the page is idle, and the buffer is flushed into it.
 * Errors during boot are the ones worth catching, so dropping them to save the
 * bytes would defeat the point.
 *
 * Noise control matters here as much as capture does. A public storefront gets
 * a steady drip of junk from browser extensions and injected third-party
 * scripts; left unfiltered it would eat the monthly event allowance and start
 * dropping real errors. Hence the ignore/deny lists below.
 */

const DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Environments allowed to report. A developer already has the console open, so
 * local browsing buys nothing and costs quota; worse, on 2026-07-28 sixty local
 * errors landed in the production project and read as live traffic until the
 * URLs were checked.
 */
const REPORTING_ENVIRONMENTS = ['production', 'staging'];

/** Cap the buffer: if something is erroring in a loop, we do not want the array to grow without bound. */
const MAX_BUFFERED = 20;

const buffered = [];

let environment = '';
let release = '';

function meta(name) {
    return document.querySelector(`meta[name="${name}"]`)?.content?.trim() ?? '';
}

function bufferError(event) {
    if (buffered.length < MAX_BUFFERED) {
        buffered.push(event.error ?? event.message);
    }
}

function bufferRejection(event) {
    if (buffered.length < MAX_BUFFERED) {
        buffered.push(event.reason);
    }
}

/** Errors we never want to pay for: extensions, injected scripts, and browser quirks. */
const IGNORE_ERRORS = [
    // Fired when a cross-origin script throws; carries no stack, so it is unactionable.
    'Script error.',
    // Chrome's benign resize-loop warning, not a real fault.
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    // Aborted navigations and fetches the user caused by leaving the page.
    'AbortError',
    'The operation was aborted',
    'Non-Error promise rejection captured',
];

const DENY_URLS = [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
    /extensions\//i,
];

async function loadSentry() {
    window.removeEventListener('error', bufferError);
    window.removeEventListener('unhandledrejection', bufferRejection);

    try {
        // Named imports, not a namespace import, so the bundler can drop
        // everything we do not call.
        const { init, captureException } = await import('@sentry/browser');

        init({
            dsn: DSN,
            environment,
            // Ties a browser error to the deploy that shipped it, so "this
            // started at 14:00" can be answered with a build, not a guess.
            release: release || undefined,
            // Errors only. Tracing, replay and profiling all cost quota and
            // bytes, and none of them answer "did something break".
            tracesSampleRate: 0,
            ignoreErrors: IGNORE_ERRORS,
            denyUrls: DENY_URLS,
        });

        for (const captured of buffered) {
            captureException(captured);
        }
        buffered.length = 0;
    } catch {
        // Reporting must never be the thing that breaks the page. If the chunk
        // fails to load (offline, blocked), the site carries on unreported.
    }
}

export function initErrorReporting() {
    environment = meta('sentry-environment');
    release = meta('sentry-release');

    if (!DSN || !REPORTING_ENVIRONMENTS.includes(environment)) {
        return;
    }

    window.addEventListener('error', bufferError);
    window.addEventListener('unhandledrejection', bufferRejection);

    const start = () => {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(loadSentry, { timeout: 5000 });
        } else {
            setTimeout(loadSentry, 2000);
        }
    };

    if (document.readyState === 'complete') {
        start();
    } else {
        window.addEventListener('load', start, { once: true });
    }
}
